import { type LocationDescriptor } from 'history';
import { type MouseEvent } from 'react';

import { api, isDefined } from 'common';

export function shouldOpenInNewTab(event: MouseEvent) {
  return event.ctrlKey || event.metaKey;
}

export function getPathFromLocationDescriptor(locationDescriptor: LocationDescriptor) {
  return typeof locationDescriptor === 'string'
    ? locationDescriptor
    : [locationDescriptor.pathname, locationDescriptor.search].join('?');
}

function buildHashRouteHref(locationDescriptor: LocationDescriptor) {
  const pathname = getPathFromLocationDescriptor(locationDescriptor);
  return new URL(`#${pathname}`, window.location.href).href;
}

export function openRouteInNewTab(locationDescriptor: LocationDescriptor) {
  window.open(buildHashRouteHref(locationDescriptor), '_blank', 'noopener,noreferrer');
}

/**
 * Opens a blank tab synchronously. Browsers only open `window.open` as a tab
 * (rather than a popup window) when it is called within a live user gesture, so
 * a tab that must be opened after `await` work should be opened up front with
 * this function and navigated afterwards with `navigateBrowserTabToRoute`.
 */
export function openBlankBrowserTab(): Window | null {
  const tab = window.open('', '_blank');
  // Sever the opener link immediately (reverse-tabnabbing) rather than waiting
  // for navigation, which may be delayed or never happen.
  if (isDefined(tab)) {
    tab.opener = null;
  }
  return tab;
}

/** Requires a `Content-Disposition: attachment` response; anything renderable unloads the app. */
export function startBrowserDownload(downloadUrl: string) {
  window.location.assign(downloadUrl);
}

export function navigateBrowserTabToRoute(tab: Window, locationDescriptor: LocationDescriptor) {
  tab.opener = null;
  tab.location.href = buildHashRouteHref(locationDescriptor);
}

export function formatSearchResultLink(result: api.Search.Result) {
  switch (result.entity.type) {
    case api.Search.Entity.Encounter:
      return `/entity/encounters/${result.id}`;
    case api.Search.Entity.Order:
      return `/entity/orders/${result.id}`;
    case api.Search.Entity.Patient:
      return `/entity/patients/${result.id}`;
    case api.Search.Entity.ExternalDocumentPacket:
      return `/entity/externalDocumentPackets/${result.id}`;
  }
}
